/**
 * Светофор дня — самый верх дашборда, всегда виден.
 * Сжимает всю срочность в один цвет + одну фразу-команду.
 * level вычисляется на бэке как агрегат тех же счётчиков, что и блок «что горит».
 * Здесь есть fallback-вычисление из counts, если backend не прислал status_banner.
 */

import { AlertTriangle, CheckCircle2, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Dashboard, StatusBanner as StatusBannerData, StatusLevel } from '@/types'

const STYLE: Record<StatusLevel, { wrap: string; iconWrap: string; title: string }> = {
  red: {
    wrap: 'border-red-300 bg-red-50',
    iconWrap: 'bg-red-500',
    title: 'Горит — нужно действовать сейчас',
  },
  amber: {
    wrap: 'border-amber-300 bg-amber-50',
    iconWrap: 'bg-amber-500',
    title: 'Есть, на что обратить внимание',
  },
  green: {
    wrap: 'border-green-300 bg-green-50',
    iconWrap: 'bg-green-500',
    title: 'Всё под контролем',
  },
}

function Icon({ level }: { level: StatusLevel }) {
  if (level === 'red') return <AlertTriangle className="h-5 w-5 text-white" />
  if (level === 'amber') return <Clock className="h-5 w-5 text-white" />
  return <CheckCircle2 className="h-5 w-5 text-white" />
}

/** Fallback: собрать светофор из counts, если backend не прислал status_banner. */
function deriveBanner(d: Dashboard): StatusBannerData {
  const sla = d.counts?.sla_flags ?? 0
  const unclaimed = d.counts?.unclaimed_leads ?? 0
  const noStep = d.counts?.no_next_step_deals ?? 0

  // red если есть просрочка SLA или «старый» (>30 мин) непринятый лид
  const hasOldLead = (d.unclaimed_leads ?? []).some(
    (l) => Date.now() - new Date(l.created_at).getTime() > 30 * 60 * 1000,
  )

  let level: StatusLevel = 'green'
  if (sla > 0 || hasOldLead) level = 'red'
  else if (unclaimed > 0 || noStep > 0) level = 'amber'

  const parts: string[] = []
  if (sla > 0) parts.push(`${sla} ${plural(sla, 'сделка горит', 'сделки горят', 'сделок горят')} по SLA`)
  if (unclaimed > 0) parts.push(`${unclaimed} ${plural(unclaimed, 'лид ждёт', 'лида ждут', 'лидов ждут')} взятия`)
  if (noStep > 0)
    parts.push(`${noStep} ${plural(noStep, 'сделка', 'сделки', 'сделок')} без следующего шага`)

  return {
    level,
    text: parts.length ? parts.join(', ') : 'Очередь пуста — горящих задач нет',
  }
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}

export function StatusBanner({ dashboard }: { dashboard: Dashboard }) {
  const banner = dashboard.status_banner ?? deriveBanner(dashboard)
  const style = STYLE[banner.level]

  return (
    <div className={cn('flex items-center gap-4 rounded-xl border p-4 shadow-sm', style.wrap)}>
      <div className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-full', style.iconWrap)}>
        <Icon level={banner.level} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground">{style.title}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{banner.text}</p>
      </div>
    </div>
  )
}
